import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { withRetry } from "./useCreditosData";

/**
 * XML dos documentos de crédito, lido de /movimentacoes/download/:chave.
 * A API de créditos não manda o nome do produto, então ele sai do XML:
 * na NF-e, o xProd de cada item; no CT-e, o produto predominante (proPred).
 * Só funciona para itens com chave: hoje, na prática, os de frete.
 * As leituras passam por uma fila (uma por vez, com pausa) por causa do limite
 * 429 da API de dev, e ficam em cache durante a sessão.
 */
const PAUSE_MS = 150;
const xmlCache = new Map();
const infoCache = new Map();
let queue = Promise.resolve();

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export const fetchXml = (chave) => {
  if (!xmlCache.has(chave)) {
    const request = queue.then(() => withRetry(
      () => api.get(`/movimentacoes/download/${chave}`, { responseType: "text", transformResponse: [(d) => d] }),
      () => false,
    )).then((res) => String(res.data || ""));
    queue = request.catch(() => {}).then(() => sleep(PAUSE_MS));
    xmlCache.set(chave, request);
    request.catch(() => xmlCache.delete(chave));
  }
  return xmlCache.get(chave);
};

const text = (node, tag) => node.getElementsByTagName(tag)[0]?.textContent.trim() || "";

// Produtos do documento: NF-e lista cada item; CT-e tem só o produto predominante
export const parseXml = (xml) => {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const dets = [...doc.getElementsByTagName("det")].map((det) => ({
    name: text(det, "xProd"),
    ncm: text(det, "NCM"),
    value: Number(text(det, "vProd")) || 0,
  })).filter((d) => d.name);
  if (dets.length > 0) return { kind: "nfe", products: dets };
  const pred = text(doc, "proPred");
  return { kind: "cte", products: pred ? [{ name: pred, ncm: "", value: 0 }] : [] };
};

export const loadXmlInfo = (chave) => {
  if (!infoCache.has(chave)) {
    const p = fetchXml(chave).then(parseXml);
    infoCache.set(chave, p);
    p.catch(() => infoCache.delete(chave));
  }
  return infoCache.get(chave);
};

// Nome de um item de crédito dentro do seu documento: NCM e valor mais próximos
export const nameForItem = (info, item) => {
  if (!info || info.products.length === 0) return "";
  if (info.kind === "cte") return info.products[0].name;
  const sameNcm = info.products.filter((p) => item.ncm && p.ncm === String(item.ncm));
  const pool = sameNcm.length > 0 ? sameNcm : info.products;
  const target = item.valorProduto || 0;
  return pool.reduce((best, p) => (Math.abs(p.value - target) < Math.abs(best.value - target) ? p : best)).name;
};

// Resumo do documento: "Produto" ou "Produto + 3 outros"
export const documentTitle = (info) => {
  if (!info || info.products.length === 0) return "";
  const distinct = [...new Set(info.products.map((p) => p.name))];
  return distinct.length > 1 ? `${distinct[0]} + ${distinct.length - 1} ${distinct.length === 2 ? "outro" : "outros"}` : distinct[0];
};

export function useXmlInfo(chave) {
  const [state, setState] = useState({ info: null, loading: !!chave, error: false });
  useEffect(() => {
    if (!chave) { setState({ info: null, loading: false, error: false }); return undefined; }
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));
    loadXmlInfo(chave)
      .then((info) => { if (alive) setState({ info, loading: false, error: false }); })
      .catch(() => { if (alive) setState({ info: null, loading: false, error: true }); });
    return () => { alive = false; };
  }, [chave]);
  return state;
}

export async function downloadXml(chave) {
  try {
    const xml = await fetchXml(chave);
    const url = window.URL.createObjectURL(new Blob([xml], { type: "application/xml" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${chave}.xml`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Erro ao baixar XML:", err);
    toast.error(err.response?.status === 404 ? "XML não encontrado no projeto" : "Erro ao baixar o XML");
  }
}
